import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { RouteChangeListener, AssistantProvider } from '@studio/assistant';

import createAuthClient, {
  createAuth0Client,
  setSharedAuthClient,
} from '@appcues/auth';
import AuthProvider from 'studio5/contexts/auth';
import WebSocketProvider from 'studio5/contexts/websocket-api';
import GraphQLApiProvider from 'studio5/contexts/graphql-api';
import FeatureFlagsProvider from 'studio5/contexts/feature-flags';
import DevToolsProvider from 'studio5/contexts/dev-tools';

import Preconditions from 'studio5/components/Preconditions';
import LoadingOverlay from './components/LoadingOverlay';
import { ShellLayout } from './layout/ShellLayout';
import { AppRouter } from './router/AppRouter';

const Studio5Content = lazy(() => import('./entries/Studio5Content'));
const StudioClassicContent = lazy(
  () => import('./entries/StudioClassicContent')
);

const authClient =
  import.meta.env.AUTH0_ENABLED === 'true'
    ? createAuth0Client({
        domain: import.meta.env.AUTH0_DOMAIN,
        clientId: import.meta.env.AUTH0_CLIENT_ID,
        audience: import.meta.env.CUSTOMER_API_URL,
        redirectUri: window.location.href,
        baseUrl: import.meta.env.CUSTOMER_API_URL,
        crxId: import.meta.env.CRX_ID,
        scope: 'openid profile email offline_access',
      })
    : createAuthClient(
        import.meta.env.CUSTOMER_API_URL,
        import.meta.env.AUTH_APP_URL,
        import.meta.env.CRX_ID
      );

setSharedAuthClient(authClient);

const PREVIEW_PATTERN = /^\/index\.[\da-f]{40}\.html/;
const isPreviewing = PREVIEW_PATTERN.test(window.location.pathname);
const Router: typeof BrowserRouter = isPreviewing ? HashRouter : BrowserRouter;

function App() {
  useEffect(() => {
    const reload = () => window.location.reload();
    window.addEventListener('appcues:spoofed', reload);
    window.addEventListener('appcues:despoofed', reload);

    const handleNoAccount = () => {
      const noAccountPath = '/accounts-v2';
      if (
        window.location.pathname.includes(noAccountPath) ||
        window.location.hash.includes(noAccountPath)
      ) {
        return;
      }

      if (isPreviewing) {
        window.location.hash = `#${noAccountPath}`;
      } else {
        window.location.replace(noAccountPath);
      }
    };
    window.addEventListener('appcues:no-account', handleNoAccount);

    return () => {
      window.removeEventListener('appcues:spoofed', reload);
      window.removeEventListener('appcues:despoofed', reload);
      window.removeEventListener('appcues:no-account', handleNoAccount);
    };
  }, []);

  return (
    <Router>
      <AuthProvider>
        <WebSocketProvider>
          <GraphQLApiProvider>
            <FeatureFlagsProvider>
              <DevToolsProvider>
                <AssistantProvider>
                  <RouteChangeListener />
                  <Preconditions />
                  <ShellLayout>
                    <Suspense fallback={<LoadingOverlay />}>
                      <AppRouter
                        studio5Content={<Studio5Content />}
                        studioClassicContent={<StudioClassicContent />}
                      />
                    </Suspense>
                  </ShellLayout>
                </AssistantProvider>
              </DevToolsProvider>
            </FeatureFlagsProvider>
          </GraphQLApiProvider>
        </WebSocketProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
